import { useEffect, useState } from 'react';
import type { PaigeDestinationsBridge } from './paige.global';
import type { PaigeCcCatalog, PaigeCcCatalogEntry } from './product-destination-types';

/** Paige's reason when a call failed (with its Technical details), else the fallback. */
export function reasonOf(failure: unknown, fallback: string): string {
  return failure instanceof Error && failure.message ? failure.message : fallback;
}

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The most catalogs the picker holds; past it, a search finds the rest (CapturePoint's 600). */
export const CATALOG_LIMIT = 600;

/** One answer of the picker, for the search and the try it was asked for. */
interface PickerPage {
  asked: string;
  items: PaigeCcCatalogEntry[];
  cursor: string | null;
  canCreate: boolean | null;
  error: string;
}

/**
 * The catalog picker's list: searched as the person types (a moment after the last key), one page at a
 * time with Show more, and whether the key may build a catalog (the first page says). What the list shows
 * belongs to the search that asked for it; a newer search shows "loading" until its own answer comes.
 */
export function useCatalogPicker(bridge: PaigeDestinationsBridge, destinationId: string) {
  const [query, setQuery] = useState('');
  const [tries, setTries] = useState(0);
  const [page, setPage] = useState<PickerPage | null>(null);
  const asked = `${query.trim()}\u0000${tries}`;
  useEffect(() => {
    let live = true;
    const timer = window.setTimeout(() => {
      void firstPage(bridge, destinationId, query.trim()).then(
        (found) => live && setPage({ ...found, asked }),
      );
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [bridge, destinationId, query, asked]);
  const shown = page?.asked === asked ? page : null;
  const more = async () => {
    if (!shown?.cursor || !bridge.ccCatalogs) return;
    const next = await bridge.ccCatalogs(destinationId, query.trim() || undefined, shown.cursor);
    setPage({
      ...shown,
      items: [...shown.items, ...next.items].slice(0, CATALOG_LIMIT),
      cursor: next.nextCursor,
    });
  };
  return { query, setQuery, more, reload: () => setTries((n) => n + 1), ...viewOf(shown) } as const;
}

/** What the list shows for the search's own answer, or "loading" until it comes. */
function viewOf(shown: PickerPage | null) {
  if (!shown)
    return { items: [], cursor: null, canCreate: null, state: 'loading', error: '' } as const;
  const state = shown.error ? ('failed' as const) : ('loaded' as const);
  return {
    items: shown.items,
    cursor: shown.cursor,
    canCreate: shown.canCreate,
    state,
    error: shown.error,
  };
}

async function firstPage(
  bridge: PaigeDestinationsBridge,
  id: string,
  query: string,
): Promise<Omit<PickerPage, 'asked'>> {
  try {
    if (!bridge.ccCatalogs) throw new Error('Catalogs could not be loaded');
    const page = await bridge.ccCatalogs(id, query || undefined);
    return { items: page.items, cursor: page.nextCursor, canCreate: page.canCreate, error: '' };
  } catch (failure) {
    return {
      items: [],
      cursor: null,
      canCreate: null,
      error: reasonOf(failure, 'Catalogs could not be loaded'),
    };
  }
}

/** The catalog a destination names (its id, or the name of one saved before the picker), with its types and fields. */
export function useCatalog(
  bridge: PaigeDestinationsBridge,
  destinationId: string,
  catalog: string,
) {
  const wanted = catalog.trim();
  const [read, setRead] = useState<{
    wanted: string;
    definition: PaigeCcCatalog | null;
    error: string;
  } | null>(null);
  useEffect(() => {
    if (!wanted) return undefined;
    let live = true;
    resolve(bridge, destinationId, wanted).then(
      (definition) => live && setRead({ wanted, definition, error: '' }),
      (failure: unknown) =>
        live &&
        setRead({
          wanted,
          definition: null,
          error: reasonOf(failure, 'The catalog could not be read'),
        }),
    );
    return () => {
      live = false;
    };
  }, [bridge, destinationId, wanted]);
  const current = read?.wanted === wanted ? read : null;
  return { definition: current?.definition ?? null, error: current?.error ?? '' };
}

async function resolve(bridge: PaigeDestinationsBridge, id: string, catalog: string) {
  if (!bridge.ccCatalog || !bridge.ccCatalogs) throw new Error('The catalog could not be read');
  if (uuid.test(catalog)) return bridge.ccCatalog(id, catalog);
  const page = await bridge.ccCatalogs(id, catalog);
  const named = page.items.find((c) => c.name.toLowerCase() === catalog.toLowerCase());
  if (!named) throw new Error(`This integration key cannot see a catalog called ${catalog}.`);
  return bridge.ccCatalog(id, named.id);
}

/**
 * The catalog as the type map reads it (the same shape a test's choices had): its types that take
 * documents, and per type the fields Paige may fill and the ones it requires.
 */
export function choicesOf(definition: PaigeCcCatalog | null): Record<string, string[]> {
  if (!definition) return {};
  const types = definition.documentTypes.filter((t) => t.canAddDocuments);
  const own = (t: (typeof types)[number]) => t.fields.filter((f) => f.sendable && !f.lineItem);
  return Object.fromEntries([
    ['documentType', types.map((t) => t.name)],
    ...types.map((t) => [`fields:${t.name}`, own(t).map((f) => f.name)]),
    ...types.map((t) => [
      `required:${t.name}`,
      own(t)
        .filter((f) => f.required)
        .map((f) => f.name),
    ]),
  ]);
}
